import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type {
  User,
  AuthState,
  AuthContextValue,
  LoginResponse,
} from '@/types';
import { httpClient, authApi } from '@/api';
import { encerrarSessaoTerminalCompartilhado } from '@/hooks/memed/memedSessionManager';

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_REFRESHING'; payload: boolean }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'LOGIN_SUCCESS'; payload: LoginResponse }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_AUTH' };

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  isRefreshing: false,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_REFRESHING':
      return { ...state, isRefreshing: action.payload };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload
          ? {
              ...action.payload,
              isPartner: action.payload.isPartner ?? state.user?.isPartner,
              partnerId: action.payload.partnerId ?? state.user?.partnerId,
            }
          : null,
        isAuthenticated: !!action.payload,
        isLoading: false,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: {
          ...action.payload.user,
          isPartner: action.payload.user.isPartner ?? state.user?.isPartner,
          partnerId: action.payload.user.partnerId ?? state.user?.partnerId,
        },
        isAuthenticated: true,
        isLoading: false,
      };
    case 'LOGOUT':
    case 'CLEAR_AUTH':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
      };
    default:
      return state;
  }
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    httpClient.setSessionExpiredHandler(() => {
      dispatch({ type: 'CLEAR_AUTH' });
      httpClient.logout();

      const publicPaths = [
        '/',
        '/login',
        '/register',
        '/register/doctor',
        '/forgot-password',
        '/reset-password',
        '/verify-email',
        '/session-expired',
        '/unauthorized',
        '/buscar',
        '/medicos',
        '/ajuda',
        '/termos',
        '/terms',
        '/privacidade',
        '/privacy',
      ];
      const isPublic =
        publicPaths.includes(location.pathname) ||
        location.pathname.startsWith('/medicos/');

      if (!isPublic) {
        navigate('/session-expired', { replace: true });
      }
    });
  }, [navigate, location.pathname]);

  const initializeAuth = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      if (httpClient.isAuthenticated()) {
        const user = await authApi.getMe();
        dispatch({ type: 'SET_USER', payload: user });
      } else {
        dispatch({ type: 'CLEAR_AUTH' });
      }
    } catch {
      httpClient.logout();
      dispatch({ type: 'CLEAR_AUTH' });
    }
  }, []);

  useEffect(() => {
    initializeAuth().catch(() => {
      httpClient.logout();
      dispatch({ type: 'CLEAR_AUTH' });
    });
  }, [initializeAuth]);

  const login = useCallback(async (email: string, password: string, loginResponse?: LoginResponse) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = loginResponse || await authApi.login(email, password);
      httpClient.saveAccessToken(response.accessToken);
      dispatch({ type: 'LOGIN_SUCCESS', payload: response });
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    dispatch({ type: 'SET_REFRESHING', payload: true });
    try {
      const userId = state.user?.id;
      const sessionId = httpClient.getSessionIdFromToken();
      if (userId) {
        await authApi.logout(userId, sessionId || undefined);
      }
    } catch {
      // Ignore logout errors
    } finally {
      httpClient.logout();
      encerrarSessaoTerminalCompartilhado().catch(() => {});
      dispatch({ type: 'LOGOUT' });
    }
  }, [state.user?.id]);

  const logoutAll = useCallback(async () => {
    dispatch({ type: 'SET_REFRESHING', payload: true });
    try {
      const userId = state.user?.id;
      if (userId) {
        await authApi.logoutAll(userId);
      }
    } catch {
      // Ignore logout errors
    } finally {
      httpClient.logout();
      encerrarSessaoTerminalCompartilhado().catch(() => {});
      dispatch({ type: 'LOGOUT' });
    }
  }, [state.user?.id]);

  const refresh = useCallback(async () => {
    if (!httpClient.isAuthenticated()) return;

    dispatch({ type: 'SET_REFRESHING', payload: true });
    try {
      const response = await authApi.refresh();
      httpClient.saveAccessToken(response.accessToken);
      dispatch({ type: 'SET_USER', payload: response.user });
    } catch {
      httpClient.logout();
      dispatch({ type: 'CLEAR_AUTH' });
    } finally {
      dispatch({ type: 'SET_REFRESHING', payload: false });
    }
  }, []);

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    logoutAll,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
